import { useEffect, useState } from "react";
import DetailPanel from "../Details/DetailPanel";
import styles from "./DataListComponent.module.css";
import { truncateString } from "../../utils/text";
import { getBookmarks, toggleBookmarkAPI } from "../../utils/bookmark";

import {
  FaBriefcase,
  FaMapMarkerAlt,
  FaDollarSign,
  FaRegBuilding,
  FaChevronDown,
  FaChevronRight,
  FaCalendarAlt,
  FaTools,
  FaIndustry,
  FaUserTie,
} from "react-icons/fa";
import { IoBookmark, IoBookmarkOutline } from "react-icons/io5";
import LoginPromptModal from "../UI/LoginPromptModal/LoginPromptModal";
import useUserData from "../../Context/user/useUserData";

export const DataListComponent = ({
  dataListView,
  userBookmarks: externalBookmarks,
  currentPage,
  setCurrentPage,
  searchedKey,
  resultsFields2,
  searchLoading,
  totalSearchPages,
  groupBy,
  metadata,
  onBookmarkToggle,
}) => {
  const fieldIconMap = {
    jobTypeFacets: FaBriefcase,
    skillsFacets: FaTools,
    salaryFacets: FaDollarSign,
    locationFacets: FaMapMarkerAlt,
    workModelFacets: FaBriefcase,
    datePostedFacets: FaCalendarAlt,
    countryFacets: FaMapMarkerAlt,
    experienceLevelFacets: FaUserTie,
    industryFacets: FaIndustry,
  };

  const defaultIcon = FaBriefcase;

  const { user, loginWithGoogle } = useUserData();
  const isGoogleUser = !!user?.avatar;

  const [bookmarkedMap, setBookmarkedMap] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({ 0: true });

  // NEW
  const [pendingBookmark, setPendingBookmark] = useState(null);

  // Initialize bookmarks
  useEffect(() => {
    const initBookmarks = async () => {
      if (externalBookmarks) {
        const map = {};
        externalBookmarks.forEach((b) => (map[b._id] = true));

        setBookmarkedMap((prev) => ({
          ...prev,
          ...map,
        }));
        return;
      }

      if (!isGoogleUser || !user?.id) {
        return;
      }

      try {
        const data = await getBookmarks(user.id);

        const map = {};
        data.forEach((b) => (map[b._id] = true));

        // merge bookmarks instead of overwriting
        setBookmarkedMap((prev) => ({
          ...prev,
          ...map,
        }));
      } catch (err) {
        console.error("Failed to load bookmarks", err);
      }
    };

    initBookmarks();
  }, [user?.id, isGoogleUser, externalBookmarks]);

  // Close modal after login
  useEffect(() => {
    if (isGoogleUser) {
      setShowLoginModal(false);
    }
  }, [isGoogleUser]);

  // Apply pending bookmark after login
  useEffect(() => {
    const applyPendingBookmark = async () => {
      if (!isGoogleUser || !pendingBookmark || !user?.id) return;

      const item = pendingBookmark;

      // optimistic update
      setBookmarkedMap((prev) => ({
        ...prev,
        [item._id]: true,
      }));

      try {
        if (onBookmarkToggle) {
          await onBookmarkToggle(item, false);
        } else {
          await toggleBookmarkAPI({
            userId: user.id,
            jobId: item._id,
            alreadyBookmarked: false,
          });
        }
      } catch (err) {
        console.error("Bookmark failed", err);

        // rollback
        setBookmarkedMap((prev) => ({
          ...prev,
          [item._id]: false,
        }));
      }

      setPendingBookmark(null);
    };

    applyPendingBookmark();
  }, [isGoogleUser, pendingBookmark, user?.id]);

  const handleBookmarkClick = async (item) => {
    if (!isGoogleUser) {
      setPendingBookmark(item);
      setShowLoginModal(true);
      return;
    }

    const alreadyBookmarked = !!bookmarkedMap[item._id];

    setBookmarkedMap((prev) => ({
      ...prev,
      [item._id]: !alreadyBookmarked,
    }));

    try {
      if (onBookmarkToggle) {
        await onBookmarkToggle(item, alreadyBookmarked);
      } else {
        await toggleBookmarkAPI({
          userId: user?.id,
          jobId: item._id,
          alreadyBookmarked,
        });
      }
    } catch {
      setBookmarkedMap((prev) => ({
        ...prev,
        [item._id]: alreadyBookmarked,
      }));
    }
  };

  const handleChangePage = (value) => {
    setCurrentPage(value);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (searchLoading) {
      setSelectedItem(null);
      setExpandedGroups({ 0: true });
    }
  }, [searchLoading]);

  useEffect(() => {
    if (groupBy && dataListView?.length > 0) {
      setExpandedGroups({ 0: true });
    }
  }, [dataListView, groupBy]);

  const toggleGroup = (groupIndex) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupIndex]: !prev[groupIndex],
    }));
  };

  const isValidValue = (value) => {
    if (value === null || value === undefined) return false;

    if (typeof value === "string") {
      const trimmed = value.trim().toLowerCase();
      return trimmed && !["not specified", "n/a", "null"].includes(trimmed);
    }

    return true;
  };

  const getGroupFields = (group) => {
    const fields = [];

    metadata?.tabs?.forEach((tab) => {
      const { field, request_key, display_name } = tab;

      if (group.hasOwnProperty(field) && isValidValue(group[field])) {
        fields.push({
          key: request_key,
          value: group[field],
          displayName: display_name,
          icon: fieldIconMap[request_key] || defaultIcon,
        });
      }
    });

    return fields;
  };

  // const renderJobItem = (value) => {
  //   const titleValue = resultsFields2.title;
  //   const descValue = resultsFields2.summary;

  //   const bookmarked = !!bookmarkedMap[value._id] || pendingBookmark?._id === value._id;

  //   return (
  //     <div
  //       key={value._id}
  //       className={`${styles.learningDetails} ${
  //         selectedItem?._id === value._id ? styles.learningDetailsActive : ""
  //       }`}
  //     >
  //       <div className={styles.learningPath}>
  //         <div className={styles.skilDetails}>
  //           <h3 className={styles.title} onClick={() => setSelectedItem(value)}>
  //             {value[titleValue] || "No Title"}
  //           </h3>

  //           {descValue && (
  //             <p className={styles.description}>
  //               {expandedItems?.[value._id]
  //                 ? value[descValue]
  //                 : truncateString(value[descValue] || "", 400)}

  //               {value[descValue]?.length > 500 && (
  //                 <span
  //                   onClick={() =>
  //                     setExpandedItems((prev) => ({
  //                       ...prev,
  //                       [value._id]: !prev[value._id],
  //                     }))
  //                   }
  //                   className={styles.showMore}
  //                 >
  //                   {expandedItems?.[value._id] ? " Show less" : " ...Show more"}
  //                 </span>
  //               )}
  //             </p>
  //           )}

  //           <div className={styles.additionalDetails}>
  //             <div className={styles.metaCapsules}>
  //               {value?.job_type && (
  //                 <span className={styles.capsule}>
  //                   <FaBriefcase className={styles.metaIcon} />
  //                   {value.job_type}
  //                 </span>
  //               )}

  //               {value?.salary_lower_numeric > 0 && (
  //                 <span className={styles.capsule}>
  //                   <FaDollarSign className={styles.metaIcon} />
  //                   {value.salary_lower_numeric}
  //                 </span>
  //               )}

  //               {value?.company && (
  //                 <span className={styles.capsule}>
  //                   <FaRegBuilding className={styles.metaIcon} />
  //                   {value.company}
  //                 </span>
  //               )}

  //               {(value?.country?.length > 0 || value?.location?.length > 0) && (
  //                 <span className={styles.capsule}>
  //                   <FaMapMarkerAlt className={styles.metaIcon} />
  //                   {[...(value.location || []), ...(value.country || [])].join(", ")}
  //                 </span>
  //               )}
  //             </div>

  //             <div>
  //               <span className={styles.bookmarkCapsule} onClick={() => handleBookmarkClick(value)}>
  //                 {bookmarked ? (
  //                   <IoBookmark className={styles.bookmarkIconFilled} />
  //                 ) : (
  //                   <IoBookmarkOutline className={styles.bookmarkIconOutline} />
  //                 )}
  //               </span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const buildDynamicMeta = (item, metadata) => {
    const fields = [];

    const resultsFields = metadata?.resultsFields2 || {};

    // Helper to merge multiple fields based on partial match
    const mergeFields = (partialKeys) => {
      return Object.entries(resultsFields)
        .filter(([k]) => partialKeys.some((pk) => k.toLowerCase().includes(pk.toLowerCase())))
        .map(([_, field]) => item?.[field])
        .filter(Boolean)
        .flat() // in case some are arrays
        .join(", ");
    };

    // Company
    const companyValue = mergeFields(["company"]);
    if (companyValue) {
      fields.push({ key: "company", value: companyValue, icon: FaRegBuilding });
    }

    // Location
    const locationValue = mergeFields(["state", "country", "location"]);
    if (locationValue) {
      fields.push({ key: "location", value: locationValue, icon: FaMapMarkerAlt });
    }

    // Salary
    const salaryValue = mergeFields(["salary"]);
    if (salaryValue) {
      fields.push({ key: "salary", value: salaryValue, icon: FaDollarSign });
    }

    return fields;
  };

  const renderJobItem = (item) => {
    const titleKey = resultsFields2?.title;
    const summaryKey = resultsFields2?.summary;

    const title = item?.[titleKey];
    const summary = item?.[summaryKey];

    const bookmarked = !!bookmarkedMap[item._id] || pendingBookmark?._id === item._id;

    const location = [item?.city, item?.state, item?.country]?.filter(Boolean)?.join(", ") || null;
    const salary = item?.salary_lower_numeric || item?.salary || null;
    const dynamicMeta = buildDynamicMeta(item, metadata);

    return (
      <div
        key={item._id}
        className={`${styles.learningDetails} ${
          selectedItem?._id === item._id ? styles.learningDetailsActive : ""
        }`}
      >
        <div className={styles.learningPath}>
          <div className={styles.skilDetails}>
            <h3
              className={styles.title}
              style={{ cursor: "pointer", margin: 0 }}
              onClick={() =>
                setSelectedItem({
                  ...item,
                  _meta: dynamicMeta,
                })
              }
            >
              {title || "No Title"}
            </h3>

            {summary && (
              <p className={styles.description}>
                {expandedItems?.[item._id] ? summary : truncateString(summary, 300)}

                {summary.length > 300 && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedItems((prev) => ({
                        ...prev,
                        [item._id]: !prev[item._id],
                      }));
                    }}
                    className={styles.showMore}
                  >
                    {expandedItems?.[item._id] ? " Show less" : " ...Show more"}
                  </span>
                )}
              </p>
            )}

            <div className={styles.additionalDetails}>
              <div className={styles.metaCapsules}>
                {dynamicMeta.map((f, idx) => {
                  const Icon = f.icon;

                  return (
                    <span key={idx} className={styles.capsule}>
                      <Icon className={styles.metaIcon} />
                      {f.value}
                    </span>
                  );
                })}
              </div>

              <div>
                <span className={styles.bookmarkCapsule} onClick={() => handleBookmarkClick(item)}>
                  {bookmarked ? (
                    <IoBookmark className={styles.bookmarkIconFilled} />
                  ) : (
                    <IoBookmarkOutline className={styles.bookmarkIconOutline} />
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const renderGroupedView = () => (
    <>
      {dataListView?.map((group, groupIndex) => {
        const groupFields = getGroupFields(group);

        return (
          <div key={groupIndex} className={styles.accordionGroup}>
            <div
              className={`${styles.accordionHeader} ${
                expandedGroups[groupIndex] ? styles.accordionHeaderActive : ""
              }`}
              onClick={() => toggleGroup(groupIndex)}
            >
              <div className={styles.accordionHeaderContent}>
                <span className={styles.accordionIcon}>
                  {expandedGroups[groupIndex] ? <FaChevronDown /> : <FaChevronRight />}
                </span>

                <div className={styles.accordionCapsules}>
                  {groupFields.map((field) => (
                    <span key={field.key} className={styles.accordionCapsule}>
                      <field.icon className={styles.accordionCapsuleIcon} />
                      {field.value}
                    </span>
                  ))}

                  {groupFields.length === 0 && <span className={styles.accordionTitle}>Other</span>}
                </div>
              </div>

              <span className={styles.accordionCount}>
                {group.count} {group.count === 1 ? "result" : "results"}
              </span>
            </div>

            {expandedGroups[groupIndex] && (
              <div className={styles.accordionContent}>
                {group.jobs?.map((job) => renderJobItem(job))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  const renderFlatView = () => <>{dataListView?.map((value) => renderJobItem(value))}</>;

  return (
    <div className={styles.searchResultWrap}>
      <div className={styles.resultsContainer}>
        <div className={styles.exploreTopics}>
          <div className={styles.salesResult}>
            {searchLoading ? null : dataListView?.length > 0 ? (
              <>
                {groupBy ? renderGroupedView() : renderFlatView()}

                {totalSearchPages > 1 && (
                  <div className={styles.pagination}>
                    {Array.from({ length: totalSearchPages }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`${styles.pageButton} ${
                          currentPage === pageNum ? styles.active : ""
                        }`}
                        onClick={() => handleChangePage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className={styles.noResults}>
                No data found for "{searchedKey}". Try searching for others.
              </div>
            )}
          </div>
        </div>

        <DetailPanel
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          resultsFields2={resultsFields2}
        />

        {showLoginModal && (
          <LoginPromptModal
            onClose={() => setShowLoginModal(false)}
            onLogin={() => loginWithGoogle()}
          />
        )}
      </div>
    </div>
  );
};